import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CreditCard, Lock, Check, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { apiService } from '@/lib/api';
import { toast } from 'sonner';
import { AuthShell, PrimaryButton, AUTH_SUBTLE, AUTH_BLUE, AUTH_PAGE_BG } from './authShared';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const TIERS = [
  { id: 'basic', name: 'Basic', price: 29, description: 'For small projects', features: ['Up to 2 active projects', 'Bid management', 'Draw scheduling', 'Email support'], popular: false },
  { id: 'pro', name: 'Pro', price: 69, description: 'For growing businesses', features: ['Up to 5 active projects', 'Everything in Basic', 'AI budget analysis', 'Priority support'], popular: true },
  { id: 'enterprise', name: 'Enterprise', price: 199, description: 'For large operations', features: ['Unlimited projects', 'Everything in Pro', 'Cash flow forecasting', 'Dedicated support'], popular: false },
];

const TierSelector = ({ onSelect }) => (
  <div className="space-y-3" data-testid="complete-signup-tier-selector">
    {TIERS.map((tier) => (
      <button
        key={tier.id}
        onClick={() => onSelect(tier)}
        className="w-full text-left p-4 rounded-md border transition-all hover:border-slate-400"
        style={{ borderColor: tier.popular ? AUTH_BLUE : '#E4E9F0', background: tier.popular ? '#EEF2FB' : '#fff', borderWidth: tier.popular ? 2 : 1 }}
        data-testid={`complete-signup-tier-${tier.id}`}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2">
              <span className="font-semibold" style={{ color: '#13294B' }}>{tier.name}</span>
              {tier.popular && <span className="text-xs text-white px-2 py-0.5 rounded" style={{ background: AUTH_BLUE }}>Most Popular</span>}
            </div>
            <p className="text-sm mt-0.5" style={{ color: AUTH_SUBTLE }}>{tier.description}</p>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold" style={{ color: '#13294B' }}>${tier.price}</span>
            <span className="text-sm" style={{ color: AUTH_SUBTLE }}>/mo</span>
          </div>
        </div>
        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {tier.features.map((f, i) => (
            <span key={i} className="text-xs flex items-center gap-1" style={{ color: AUTH_SUBTLE }}>
              <Check className="w-3 h-3" style={{ color: '#059669' }} /> {f}
            </span>
          ))}
        </div>
      </button>
    ))}
  </div>
);

const CardForm = ({ user, selectedTier, onSuccess, onBack }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setError(null);
    try {
      const { data: intentData } = await apiService.createSetupIntent(user.id);
      if (!intentData?.client_secret) throw new Error('No client secret received from server');
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(intentData.client_secret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: { email: user.email, name: user.name || `${user.first_name || ''} ${user.last_name || ''}`.trim() },
        },
      });
      if (stripeError) {
        setError(stripeError.message);
        setIsProcessing(false);
        return;
      }
      await apiService.updatePaymentMethod(user.id, setupIntent.payment_method, selectedTier.id);
      toast.success('Payment method added! Starting your 30-day free trial.');
      onSuccess();
    } catch (err) {
      console.error('Payment setup error:', err);
      setError(err.response?.data?.detail || 'Failed to set up payment method. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="complete-signup-card-form">
      <button onClick={onBack} className="flex items-center gap-1 text-sm hover:opacity-70" style={{ color: AUTH_SUBTLE }} data-testid="complete-signup-back-btn">
        <ArrowLeft className="w-4 h-4" /> Change plan
      </button>
      <div className="p-3 rounded-md border flex items-center justify-between" style={{ borderColor: '#D6E4FF', background: '#EEF2FB' }}>
        <div>
          <span className="font-semibold" style={{ color: '#13294B' }}>{selectedTier.name} Plan</span>
          <span className="text-sm ml-2" style={{ color: AUTH_SUBTLE }}>${selectedTier.price}/mo after trial</span>
        </div>
        <span className="text-xs px-2 py-1 rounded font-medium" style={{ background: '#E7F6EF', color: '#059669' }}>30 days free</span>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 border rounded-md bg-white" style={{ borderColor: '#E4E9F0' }}>
          <CardElement options={{ style: { base: { fontSize: '16px', color: '#1e293b', '::placeholder': { color: '#94a3b8' } } } }} />
        </div>
        {error && (
          <div className="p-3 rounded-md border text-sm flex items-center gap-2" style={{ background: '#FDECEC', borderColor: '#F5C2C2', color: '#B91C1C' }} data-testid="complete-signup-error">
            <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}
        <div className="flex items-center gap-2 text-sm" style={{ color: AUTH_SUBTLE }}>
          <Lock className="w-4 h-4" /> <span>Secured with 256-bit encryption. Cancel anytime.</span>
        </div>
        <PrimaryButton type="submit" disabled={!stripe || isProcessing} data-testid="complete-signup-submit-btn" className="flex items-center justify-center gap-2">
          {isProcessing ? (<><Loader2 className="w-4 h-4 animate-spin" /> Processing...</>) : (<><CreditCard className="w-4 h-4" /> Start Free Trial - {selectedTier.name} Plan</>)}
        </PrimaryButton>
      </form>
    </div>
  );
};

function CompleteSignup({ user, onUpdateUser }) {
  const navigate = useNavigate();
  const [selectedTier, setSelectedTier] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [alreadyHasPayment, setAlreadyHasPayment] = useState(false);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!user?.id) {
        navigate('/login');
        return;
      }
      try {
        const { data } = await apiService.getSubscriptionStatus(user.id);
        if (!data.card_required) {
          setAlreadyHasPayment(true);
          setTimeout(() => navigate('/dashboard'), 1500);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setIsLoading(false);
      }
    };
    checkPaymentStatus();
  }, [user, navigate]);

  const handleSuccess = () => {
    if (onUpdateUser && user) {
      const updatedUser = { ...user, has_payment_method: true };
      onUpdateUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    navigate('/dashboard');
  };

  if (isLoading) {
    return (
      <div className="dr-marketing min-h-screen flex items-center justify-center" style={{ background: AUTH_PAGE_BG }}>
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4" style={{ color: AUTH_BLUE }} />
          <p style={{ color: AUTH_SUBTLE }}>Checking account status...</p>
        </div>
      </div>
    );
  }

  if (alreadyHasPayment) {
    return (
      <div className="dr-marketing min-h-screen flex items-center justify-center" style={{ background: AUTH_PAGE_BG }}>
        <div className="text-center">
          <div className="w-14 h-14 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#E7F6EF' }}>
            <Check className="w-8 h-8" style={{ color: '#059669' }} />
          </div>
          <h2 className="text-xl font-semibold" style={{ color: '#13294B' }}>Payment already set up</h2>
          <p style={{ color: AUTH_SUBTLE }}>Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthShell backTo={null}>
      <div className="text-center mb-6" data-testid="complete-signup-container">
        <div className="w-12 h-12 rounded-md flex items-center justify-center mx-auto mb-4" style={{ background: '#EEF2FB' }}>
          <Shield className="w-6 h-6" style={{ color: AUTH_BLUE }} />
        </div>
        <h1 className="text-2xl">{selectedTier ? 'Add payment method' : 'Complete your signup'}</h1>
        <p className="mt-1 text-[15px]" style={{ color: AUTH_SUBTLE }}>
          {selectedTier ? `You won't be charged for 30 days. Cancel anytime.` : 'Choose a plan to start your 30-day free trial. No charge until the trial ends.'}
        </p>
      </div>

      {user && (
        <div className="mb-4 p-3 rounded-md text-sm flex items-center gap-2" style={{ background: '#F8FAFC', border: '1px solid #EAECF0' }}>
          <div className="w-8 h-8 rounded-md flex items-center justify-center text-white font-semibold" style={{ background: AUTH_BLUE }}>
            {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
          </div>
          <div>
            <div className="font-medium" style={{ color: '#13294B' }}>{user.name || 'User'}</div>
            <div style={{ color: AUTH_SUBTLE }}>{user.email}</div>
          </div>
        </div>
      )}

      {!selectedTier ? (
        <TierSelector onSelect={setSelectedTier} />
      ) : (
        <Elements stripe={stripePromise}>
          <CardForm user={user} selectedTier={selectedTier} onSuccess={handleSuccess} onBack={() => setSelectedTier(null)} />
        </Elements>
      )}

      <p className="text-xs text-center mt-6" style={{ color: AUTH_SUBTLE }}>
        By continuing, you agree to our Terms of Service and Privacy Policy. Your card will be charged ${selectedTier?.price || '29-199'}/month after your 30-day trial unless you cancel.
      </p>
    </AuthShell>
  );
}

export default CompleteSignup;
